import { useState } from 'react';
import { useXClone, type XPost } from '@/state/xclone';
import { useSettings } from '@/state/settings';
import { TRIBE_COLOR, TRIBE_LABEL } from '@/content/xcloneHandles';
import { formatRelativeTime } from '@/lib/time';
import { compactNum } from '@/lib/format';
import { cn } from '@/lib/cn';
import { pickYReplyTree, type YReplyChoice } from '@/content/dialogue/yReplies';
import { sounds } from '@/audio/synth';

export function Feed() {
  const posts = useXClone((s) => s.posts);
  const toggleLike = useXClone((s) => s.toggleLike);
  const toggleRepost = useXClone((s) => s.toggleRepost);
  const replyChoice = useXClone((s) => s.replyChoice);

  if (posts.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-mono-xs text-[var(--text-muted)]">
        the feed is quiet. that won't last.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {posts.map((p) => (
        <PostRow
          key={p.id}
          post={p}
          onLike={() => toggleLike(p.id)}
          onRepost={() => toggleRepost(p.id)}
          onReplyChoice={(c) => replyChoice(p.id, c)}
        />
      ))}
    </div>
  );
}

function PostRow({ post, onLike, onRepost, onReplyChoice }: { post: XPost; onLike: () => void; onRepost: () => void; onReplyChoice: (c: YReplyChoice) => void }) {
  const tribeColor = post.authorTribe ? TRIBE_COLOR[post.authorTribe] : 'var(--accent-primary)';
  const tribeName = post.authorTribe ? TRIBE_LABEL[post.authorTribe] : null;
  const playerHandle = useSettings((s) => s.playerHandle);
  const display = post.authorIsSelf ? playerHandle : post.authorHandle;
  const [replyOpen, setReplyOpen] = useState(false);

  const tree = post.authorIsSelf ? null : pickYReplyTree({ text: post.text, tribe: post.authorTribe });

  const handleChoice = (c: YReplyChoice) => {
    onReplyChoice(c);
    setReplyOpen(false);
    sounds.notifyInfo();
  };

  return (
    <article className={cn(
      'flex gap-3 border-b border-[var(--border-subtle)] px-4 py-3 hover:bg-[var(--bg-surface)] transition-colors',
      !post.read && !post.authorIsSelf && 'bg-[var(--bg-surface)]',
    )}>
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-mono-xs font-bold text-black"
        style={{ background: tribeColor }}
      >
        {display.charAt(0).toUpperCase()}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline gap-2">
          <span className="text-mono-sm font-bold text-[var(--text-primary)]">@{display}</span>
          {tribeName && <span className="text-mono-xs" style={{ color: tribeColor }}>· {tribeName}</span>}
          <span className="ml-auto text-mono-xs text-[var(--text-muted)]">{formatRelativeTime(post.timestamp)}</span>
        </div>
        <p className="text-mono-sm text-[var(--text-primary)] whitespace-pre-wrap break-words">
          {post.text}
        </p>
        <div className="mt-1 flex items-center gap-4 text-mono-xs text-[var(--text-muted)]">
          <button
            onClick={() => setReplyOpen((v) => !v)}
            className="flex items-center gap-1 hover:text-[var(--accent-primary)]"
            aria-label="Reply"
          >
            <span>↩</span>
            <span>reply</span>
          </button>
          <button
            onClick={onRepost}
            className={cn(
              'flex items-center gap-1 hover:text-[var(--positive)]',
              post.repostedBySelf && 'text-[var(--positive)]',
            )}
            aria-label="Repost"
          >
            <span>↻</span>
            <span className="tabular-nums">{compactNum(post.reposts)}</span>
          </button>
          <button
            onClick={onLike}
            className={cn(
              'flex items-center gap-1 hover:text-[var(--negative)]',
              post.likedBySelf && 'text-[var(--negative)]',
            )}
            aria-label="Like"
          >
            <span>♥</span>
            <span className="tabular-nums">{compactNum(post.likes)}</span>
          </button>
        </div>
        {replyOpen && tree && (
          <div className="mt-2 flex flex-col gap-1.5">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">replying to @{display}</div>
            <div className="flex flex-wrap gap-1.5">
              {tree.choices.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleChoice(c)}
                  className="rounded-full border border-[var(--border-strong)] bg-[var(--bg-deep)] px-3 py-1 text-mono-xs text-[var(--text-primary)] hover:bg-[var(--accent-primary)] hover:text-black transition-colors"
                >
                  {c.label}
                </button>
              ))}
              <button
                onClick={() => setReplyOpen(false)}
                className="rounded px-3 py-1 text-mono-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                cancel
              </button>
            </div>
          </div>
        )}
        {replyOpen && !tree && (
          <p className="mt-2 text-mono-xs italic text-[var(--text-muted)]">you can\'t reply to your own post.</p>
        )}
      </div>
    </article>
  );
}
